import React from "react";
import { ModalForm, useModalDispatch } from "collections/Modal";
import {
  Field,
  FieldProps,
  Form,
  Input,
  StandardFormSelect
} from "collections/Form";
import { shortCodeString } from "collections/Form/validations";
import { InferType, mixed, object, string } from "yup";
import i18n, { useTranslation } from "utilities/i18n";
import { useCreateAltInstanceMutation } from "generated/graphql";
import { DISMISS, useNotification } from "collections/Notifications";
import { AvailableProductOptionTypes } from "utilities/licenseServerUtils";
import { CancelButton, DetailsLoader, SubmitButton } from "elements";

const CreateAltInstanceSchema = object().shape({
  name: string()
    .label(i18n.t("NAME"))
    .required(),
  productId: mixed<string>()
    .label(i18n.t("TYPE"))
    .oneOf(AvailableProductOptionTypes.map(entry => entry.value))
    .required(),
  productVersion: string()
    .label(i18n.t("VERSION_ALT"))
    .required(),
  serialNumber: string()
    .label(i18n.t("SERIAL_NUMBER"))
    .ensure(),
  hostName: string()
    .label(i18n.t("HOST_NAME"))
    .ensure(),
  encodedInstanceId: shortCodeString()
    .label(i18n.t("ENCODED_REQUEST"))
    .required()
});

export type CreateAltInstanceFormProps = InferType<
  typeof CreateAltInstanceSchema
>;

export const CreateAltInstanceModal: React.FC<{
  refresh: () => void;
  tenantId: string | undefined;
}> = ({ tenantId, refresh }) => {
  const { t } = useTranslation();
  const [, createAltInstance] = useCreateAltInstanceMutation();
  const closeModal = useModalDispatch();
  const [addNotification] = useNotification();

  const initialValues: CreateAltInstanceFormProps = {
    name: "",
    productId: "",
    productVersion: "",
    serialNumber: "",
    hostName: "",
    encodedInstanceId: ""
  };

  const onSubmit = async (values: CreateAltInstanceFormProps) => {
    const instanceResponse = await createAltInstance({
      createFromData: {
        encodedInstanceId: values.encodedInstanceId,
        fromInstanceData: {
          tenantId: tenantId || "",
          niceName: values.name,
          productId: values.productId,
          productVersion: values.productVersion,
          serialNumber: values.serialNumber,
          hostname: values.hostName
        }
      }
    });

    if (instanceResponse.data?.createAltInstance?.recordId) {
      closeModal();
      refresh();

      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message: t("SUCCESSFULLY_ADDED_ALT_INSTANCE"),
        persistThroughNav: true
      });
    } else if (instanceResponse.error) {
      addNotification({
        type: "error",
        dismiss: DISMISS.DEFAULT,
        message: t("CREATE_ALT_INSTANCE_ERROR", {
          message: instanceResponse.error.message
        })
      });
    }
  };

  return (
    <ModalForm
      title={t("ADD_ALT_INSTANCE")}
      initialValues={initialValues}
      onSubmit={onSubmit}
      name={"CreateAltInstanceModal"}
      validationSchema={CreateAltInstanceSchema}
    >
      {({ isSubmitting }) => (
        <Form>
          <DetailsLoader
            loading={isSubmitting}
            title={t("ADDING_ALT_INSTANCE")}
          >
            <div className="form-content">
              <Input
                data-testid="name"
                name="name"
                label={t("NAME")}
                tabIndex={1}
              />
              <StandardFormSelect
                name="productId"
                label={t("TYPE")}
                options={AvailableProductOptionTypes}
                tabIndex={2}
                inline={false}
                clearable={true}
              />
              <Input
                data-testid="productVersion"
                name="productVersion"
                label={t("VERSION_ALT")}
                tabIndex={3}
              />
              <Input
                data-testid="serialNumber"
                name="serialNumber"
                label={t("SERIAL_NUMBER_OPTIONAL")}
                tabIndex={4}
              />
              <Input
                data-testid="hostName"
                name="hostName"
                label={t("HOST_NAME_OPTIONAL")}
                tabIndex={5}
              />
              <Input
                data-testid="encodedInstanceId"
                name="encodedInstanceId"
                label={t("ENCODED_REQUEST")}
                tabIndex={6}
              />
            </div>
          </DetailsLoader>
          <div className="btn-container">
            <CancelButton onClick={closeModal} />
            <Field name="submit">
              {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
                <SubmitButton
                  testId="save"
                  disabled={isSubmitting || !isValid || !dirty}
                  isSubmitting={isSubmitting}
                  labelKey="ADD_ALT_INSTANCE"
                />
              )}
            </Field>
          </div>
        </Form>
      )}
    </ModalForm>
  );
};
